import React from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {fetchSourceFile} from './sourcesApi';
import {buildSourceFileInfoRows} from './sourcesFileMetadata';

/**
 * Finder/Explorer-style "Get Info" dialog for a source file.
 * Shows name, type, and metadata (Created / Uploaded / Updated / Size).
 * Seeds rows from the list-row data, then refreshes from the detail endpoint.
 */
const SourcesFileInfoDialog = ({open, onClose, file, userId}) => {
    const [detail, setDetail] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        if (!open || !file?.id || !userId) {
            setDetail(null);
            setIsLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        setIsLoading(true);

        const unwrap = (payload) => payload?.item || payload?.data || payload?.result || payload || {};

        fetchSourceFile({fileId: file.id, userId, signal: controller.signal})
            .then(async (payload) => {
                const item = unwrap(payload);

                // Reference rows (type=reference) carry no size/mime of their own — those
                // live on the canonical FILE they point to. Merge canonical metadata in.
                const canonicalId = item?.referencedDataItemId ?? file?.canonicalFileId;
                const needsCanonical = canonicalId
                    && (item?.size == null || !item?.mimeType)
                    && String(canonicalId) !== String(file.id);

                if (!needsCanonical) {
                    return item;
                }

                try {
                    const canonicalPayload = await fetchSourceFile({fileId: canonicalId, userId, signal: controller.signal});
                    const canonical = unwrap(canonicalPayload);
                    return {
                        ...canonical,
                        ...item,
                        size: item?.size ?? canonical?.size,
                        mimeType: item?.mimeType || canonical?.mimeType,
                        publishDate: item?.publishDate || canonical?.publishDate,
                    };
                } catch (canonicalError) {
                    if (canonicalError?.name === 'AbortError') {
                        throw canonicalError;
                    }
                    return item;
                }
            })
            .then((mergedItem) => {
                setDetail(mergedItem);
            })
            .catch((error) => {
                if (error?.name !== 'AbortError') {
                    setDetail(null);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, [open, file?.id, file?.canonicalFileId, userId]);

    const rows = React.useMemo(
        () => buildSourceFileInfoRows(detail || {
            ...(file?.raw || {}),
            name: file?.name,
            mimeType: file?.mimeType,
            extension: file?.extension,
            fileType: file?.fileType,
        }),
        [detail, file?.raw, file?.name, file?.mimeType, file?.extension, file?.fileType],
    );

    const fileName = file?.name || detail?.name || 'File';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-label="File info">
            <DialogTitle sx={{pr: 6}}>
                <Typography variant="subtitle1" noWrap title={fileName} sx={{fontWeight: 600}}>
                    {fileName}
                </Typography>
                <IconButton
                    aria-label="Close info"
                    onClick={onClose}
                    size="small"
                    sx={{position: 'absolute', top: 12, right: 12}}
                >
                    <CloseRoundedIcon fontSize="small"/>
                </IconButton>
            </DialogTitle>
            <Divider/>
            <DialogContent>
                {isLoading && rows.length === 0 ? (
                    <Stack alignItems="center" justifyContent="center" py={3}>
                        <CircularProgress size={24}/>
                    </Stack>
                ) : rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No additional information available.
                    </Typography>
                ) : (
                    <Stack spacing={1.25}>
                        {rows.map((row) => (
                            <Stack
                                key={row.label}
                                direction="row"
                                alignItems="baseline"
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem'}}
                                >
                                    {row.label}
                                </Typography>
                                <Box sx={{flex: 1, minWidth: 0, textAlign: 'right'}}>
                                    <Typography variant="body2" sx={{fontWeight: 500}} noWrap title={row.value}>
                                        {row.value}
                                    </Typography>
                                </Box>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SourcesFileInfoDialog;
